import * as z from 'zod';

import * as Types from './graphql';

type Properties<T> = Required<{
  [K in keyof T]: z.ZodType<T[K]>;
}>;

type definedNonNullAny = {};

export const isDefinedNonNullAny = (v: any): v is definedNonNullAny => v !== undefined && v !== null;

export const definedNonNullAnySchema = z.any().refine((v) => isDefinedNonNullAny(v));

export const ModelSortDirectionSchema = z.enum(Types.ModelSortDirection);

export function CalendarAndResultsGliderRoundInputSchema(): z.ZodObject<
  Properties<Types.CalendarAndResultsGliderRoundInput>
> {
  return z.object({
    competitionPhase: z.string().nullish(),
    round: z.number().nullish(),
    status: z.string().nullish(),
  });
}

export function CalendarAndResultsMatchDayListInputSchema(): z.ZodObject<
  Properties<Types.CalendarAndResultsMatchDayListInput>
> {
  return z.object({
    competitionId: z.string(),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    customCompetitionName: z.string().nullish(),
    date: z.string().nullish(),
    matchList: z.array(z.lazy(() => CalendarAndResultsMatchListInputSchema().nullable())).nullish(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    seasonId: z.string(),
  });
}

export function CalendarAndResultsMatchListInputSchema(): z.ZodObject<
  Properties<Types.CalendarAndResultsMatchListInput>
> {
  return z.object({
    away: z.lazy(() => CalendarAndResultsTeamInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => CalendarAndResultsTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    liveOnSky: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CalendarAndResultsTeamInputSchema(): z.ZodObject<Properties<Types.CalendarAndResultsTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    homepageUrl: z.string().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    seoName: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function CardCompetitionCalendarMatchDayInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionCalendarMatchDayInput>
> {
  return z.object({
    date: z.string().nullish(),
    matchList: z.array(z.lazy(() => CardCompetitionCalendarMatchInputSchema().nullable())).nullish(),
  });
}

export function CardCompetitionCalendarMatchInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionCalendarMatchInput>
> {
  return z.object({
    away: z.lazy(() => CardCompetitionCalendarMatchTeamInputSchema().nullish()),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => CardCompetitionCalendarMatchTeamInputSchema().nullish()),
    id: z.string().nullish(),
    link: z.string().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CardCompetitionCalendarMatchRoundInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionCalendarMatchRoundInput>
> {
  return z.object({
    competitionPhase: z.string().nullish(),
    matchDayList: z.array(z.lazy(() => CardCompetitionCalendarMatchDayInputSchema().nullable())).nullish(),
    round: z.number().nullish(),
  });
}

export function CardCompetitionCalendarMatchTeamInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionCalendarMatchTeamInput>
> {
  return z.object({
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    seoName: z.string().nullish(),
  });
}

export function CardCompetitionRankingGroupInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionRankingGroupInput>
> {
  return z.object({
    roundName: z.string().nullish(),
    teams: z.array(z.lazy(() => CardCompetitionRankingTeamInputSchema().nullable())).nullish(),
  });
}

export function CardCompetitionRankingTeamInputSchema(): z.ZodObject<
  Properties<Types.CardCompetitionRankingTeamInput>
> {
  return z.object({
    games: z.number().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    points: z.number().nullish(),
    position: z.number().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
    trend: z.string().nullish(),
  });
}

export function CompetitionBoardLinkedMatchInputSchema(): z.ZodObject<
  Properties<Types.CompetitionBoardLinkedMatchInput>
> {
  return z.object({
    away: z.lazy(() => CompetitionBoardTeamInputSchema().nullish()),
    competitionPhaseRound: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => CompetitionBoardTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CompetitionBoardPhaseRoundInputSchema(): z.ZodObject<
  Properties<Types.CompetitionBoardPhaseRoundInput>
> {
  return z.object({
    away: z.lazy(() => CompetitionBoardTeamInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    gamesPhaseCount: z.number().nullish(),
    home: z.lazy(() => CompetitionBoardTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    linkedMatch: z.lazy(() => CompetitionBoardLinkedMatchInputSchema().nullish()),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CompetitionBoardTeamInputSchema(): z.ZodObject<Properties<Types.CompetitionBoardTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    prevMatch: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    shortName: z.string().nullish(),
  });
}

export function CompetitionCalendarAndResultsMatchDayInputSchema(): z.ZodObject<
  Properties<Types.CompetitionCalendarAndResultsMatchDayInput>
> {
  return z.object({
    date: z.string().nullish(),
    matchList: z.array(z.lazy(() => CompetitionCalendarAndResultsMatchInputSchema().nullable())).nullish(),
  });
}

export function CompetitionCalendarAndResultsMatchInputSchema(): z.ZodObject<
  Properties<Types.CompetitionCalendarAndResultsMatchInput>
> {
  return z.object({
    away: z.lazy(() => CompetitionCalendarAndResultsTeamInputSchema().nullish()),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => CompetitionCalendarAndResultsTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    liveOnSky: z.string().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CompetitionCalendarAndResultsMatchRoundInputSchema(): z.ZodObject<
  Properties<Types.CompetitionCalendarAndResultsMatchRoundInput>
> {
  return z.object({
    competitionPhase: z.string().nullish(),
    matchDayList: z.array(z.lazy(() => CompetitionCalendarAndResultsMatchDayInputSchema().nullable())).nullish(),
    round: z.number().nullish(),
    status: z.string().nullish(),
  });
}

export function CompetitionCalendarAndResultsTeamInputSchema(): z.ZodObject<
  Properties<Types.CompetitionCalendarAndResultsTeamInput>
> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    seoName: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function CompetitionRankingGroupInputSchema(): z.ZodObject<Properties<Types.CompetitionRankingGroupInput>> {
  return z.object({
    roundName: z.string().nullish(),
    teams: z.array(z.lazy(() => CompetitionRankingTeamInputSchema().nullable())).nullish(),
  });
}

export function CompetitionRankingLastMatchesTrendInputSchema(): z.ZodObject<
  Properties<Types.CompetitionRankingLastMatchesTrendInput>
> {
  return z.object({
    away: z.lazy(() => CompetitionRankingTrendInputSchema().nullish()),
    home: z.lazy(() => CompetitionRankingTrendInputSchema().nullish()),
    label: z.string().nullish(),
  });
}

export function CompetitionRankingRankingListInputSchema(): z.ZodObject<
  Properties<Types.CompetitionRankingRankingListInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    groups: z.array(z.lazy(() => CompetitionRankingGroupInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CompetitionRankingTeamInputSchema(): z.ZodObject<Properties<Types.CompetitionRankingTeamInput>> {
  return z.object({
    games: z.number().nullish(),
    gamesDraw: z.number().nullish(),
    gamesLost: z.number().nullish(),
    gamesWon: z.number().nullish(),
    goalsConceded: z.number().nullish(),
    goalsDifference: z.number().nullish(),
    goalsScored: z.number().nullish(),
    id: z.string(),
    lastMatchesTrend: z.array(z.lazy(() => CompetitionRankingLastMatchesTrendInputSchema().nullable())).nullish(),
    logoUrl: z.string().nullish(),
    points: z.number().nullish(),
    position: z.number().nullish(),
    qualification: z.string().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
    trend: z.string().nullish(),
  });
}

export function CompetitionRankingTrendInputSchema(): z.ZodObject<Properties<Types.CompetitionRankingTrendInput>> {
  return z.object({
    goal: z.number().nullish(),
    name: z.string().nullish(),
  });
}

export function CompetitionReportsListInputSchema(): z.ZodObject<Properties<Types.CompetitionReportsListInput>> {
  return z.object({
    competitionPhase: z.string().nullish(),
    matchList: z.array(z.lazy(() => CompetitionReportsMatchInputSchema().nullable())).nullish(),
    reportsDetailsUrl: z.string().nullish(),
    round: z.number().nullish(),
  });
}

export function CompetitionReportsMatchInputSchema(): z.ZodObject<Properties<Types.CompetitionReportsMatchInput>> {
  return z.object({
    away: z.lazy(() => CompetitionReportsMatchTeamInputSchema().nullish()),
    date: z.string().nullish(),
    home: z.lazy(() => CompetitionReportsMatchTeamInputSchema().nullish()),
    matchId: z.string().nullish(),
    reportStatus: z.string().nullish(),
    reportUrl: z.string().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    statusLabel: z.string().nullish(),
  });
}

export function CompetitionReportsMatchTeamInputSchema(): z.ZodObject<
  Properties<Types.CompetitionReportsMatchTeamInput>
> {
  return z.object({
    goal: z.number().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    seoName: z.string().nullish(),
    teamId: z.string().nullish(),
  });
}

export function CompetitionScorersRankingScorerInputSchema(): z.ZodObject<
  Properties<Types.CompetitionScorersRankingScorerInput>
> {
  return z.object({
    areaIn: z.number().nullish(),
    areaOut: z.number().nullish(),
    goal: z.number().nullish(),
    head: z.number().nullish(),
    logoUrl: z.string().nullish(),
    penalty: z.number().nullish(),
    playerId: z.string().nullish(),
    playerName: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    position: z.number().nullish(),
    seoTeamName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamUrl: z.string().nullish(),
  });
}

export function CompetitionTeamCompetitionInputSchema(): z.ZodObject<
  Properties<Types.CompetitionTeamCompetitionInput>
> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonLabel: z.string().nullish(),
    teams: z.array(z.lazy(() => CompetitionTeamTeamInputSchema().nullable())).nullish(),
  });
}

export function CompetitionTeamTeamInputSchema(): z.ZodObject<Properties<Types.CompetitionTeamTeamInput>> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function CreateCalendarAndResultsGliderInputSchema(): z.ZodObject<
  Properties<Types.CreateCalendarAndResultsGliderInput>
> {
  return z.object({
    competitionId: z.string(),
    currentRound: z.number().nullish(),
    rounds: z.array(z.lazy(() => CalendarAndResultsGliderRoundInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateCardCompetitionCalendarInputSchema(): z.ZodObject<
  Properties<Types.CreateCardCompetitionCalendarInput>
> {
  return z.object({
    calendarAndResultsLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    matchRoundList: z.array(z.lazy(() => CardCompetitionCalendarMatchRoundInputSchema().nullable())).nullish(),
  });
}

export function CreateCardCompetitionRankingInputSchema(): z.ZodObject<
  Properties<Types.CreateCardCompetitionRankingInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    groups: z.array(z.lazy(() => CardCompetitionRankingGroupInputSchema().nullable())).nullish(),
    rankingLink: z.string().nullish(),
  });
}

export function CreateCompetitionBoardInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionBoardInput>> {
  return z.object({
    boardSponsorLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    matchList: z.array(z.lazy(() => CompetitionBoardPhaseRoundInputSchema().nullable())).nullish(),
  });
}

export function CreateCompetitionCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.CreateCompetitionCalendarAndResultsInput>
> {
  return z.object({
    competitionId: z.string(),
    currentRound: z.number().nullish(),
    customCompetitionName: z.string().nullish(),
    matchRoundList: z.array(z.lazy(() => CompetitionCalendarAndResultsMatchRoundInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateCompetitionInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any(),
    editorialDataId: z.string(),
    previousSeasonAndCompId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateCompetitionRankingInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionRankingInput>> {
  return z.object({
    competitionId: z.string(),
    rankingLists: z.array(z.lazy(() => CompetitionRankingRankingListInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateCompetitionReportsInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionReportsInput>> {
  return z.object({
    competitionId: z.string(),
    reports: z.array(z.lazy(() => CompetitionReportsListInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateCompetitionScorersRankingInputSchema(): z.ZodObject<
  Properties<Types.CreateCompetitionScorersRankingInput>
> {
  return z.object({
    competitionId: z.string(),
    competitionScorersUrl: z.string().nullish(),
    competitionSeoName: z.string().nullish(),
    page: z.string(),
    pages: z.array(z.string().nullable()).nullish(),
    scorers: z.array(z.lazy(() => CompetitionScorersRankingScorerInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateCompetitionTeamInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionTeamInput>> {
  return z.object({
    competitions: z.array(z.lazy(() => CompetitionTeamCompetitionInputSchema().nullable())).nullish(),
    id: z.string().nullish(),
  });
}

export function CreateCrossLinkInputSchema(): z.ZodObject<Properties<Types.CreateCrossLinkInput>> {
  return z.object({
    crossLinkJson: z.any(),
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function CreateEditorialDataInputSchema(): z.ZodObject<Properties<Types.CreateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any(),
    isCurrent: z.boolean().nullish(),
    isPrimary: z.boolean().nullish(),
    seasonAndCompId: z.string(),
  });
}

export function CreateLastAndNextMatchInputSchema(): z.ZodObject<Properties<Types.CreateLastAndNextMatchInput>> {
  return z.object({
    lastMatch: z.lazy(() => LastAndNextMatchMatchInputSchema().nullish()),
    nextMatch: z.lazy(() => LastAndNextMatchMatchInputSchema().nullish()),
    teamId: z.string(),
  });
}

export function CreateLiveScoreInputSchema(): z.ZodObject<Properties<Types.CreateLiveScoreInput>> {
  return z.object({
    currentDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
    id: z.string().nullish(),
    nextDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
    previousDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
  });
}

export function CreateLmpHeroInputSchema(): z.ZodObject<Properties<Types.CreateLmpHeroInput>> {
  return z.object({
    away: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    competition: z.lazy(() => LmpHeroCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    hasFollowMatch: z.boolean().nullish(),
    home: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    statusStartDateTime: z.string().datetime().nullish(),
    venue: z.string().nullish(),
  });
}

export function CreateLmpHeroStickyInputSchema(): z.ZodObject<Properties<Types.CreateLmpHeroStickyInput>> {
  return z.object({
    away: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    competition: z.lazy(() => LmpHeroCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    statusStartDateTime: z.string().datetime().nullish(),
    venue: z.string().nullish(),
  });
}

export function CreateLmpLineupInputSchema(): z.ZodObject<Properties<Types.CreateLmpLineupInput>> {
  return z.object({
    away: z.lazy(() => LmpLineupTeamInputSchema().nullish()),
    home: z.lazy(() => LmpLineupTeamInputSchema().nullish()),
    matchId: z.string(),
    referees: z.array(z.lazy(() => LmpLineupRefereeInputSchema().nullable())).nullish(),
    status: z.string().nullish(),
  });
}

export function CreateLmpMatchChangeInputSchema(): z.ZodObject<Properties<Types.CreateLmpMatchChangeInput>> {
  return z.object({
    matchId: z.string(),
    matchList: z.array(z.lazy(() => LmpMatchChangeMatchInputSchema().nullable())).nullish(),
  });
}

export function CreateLmpPredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.CreateLmpPredictedLineupDetailsInput>
> {
  return z.object({
    away: z.lazy(() => LmpPredictedLineupDetailsTeamInputSchema().nullish()),
    date: z.string().nullish(),
    home: z.lazy(() => LmpPredictedLineupDetailsTeamInputSchema().nullish()),
    matchId: z.string(),
    round: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function CreateLmpPreviewTextInputSchema(): z.ZodObject<Properties<Types.CreateLmpPreviewTextInput>> {
  return z.object({
    away: z.lazy(() => LmpPreviewTextDataTeamInputSchema().nullish()),
    calendar: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    competition: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    competitionName: z.string().nullish(),
    home: z.lazy(() => LmpPreviewTextDataTeamInputSchema().nullish()),
    matchId: z.string(),
    matchStartDateTime: z.string().nullish(),
    ranking: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    results: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    venue: z.string().nullish(),
  });
}

export function CreateLmpStatisticsInputSchema(): z.ZodObject<Properties<Types.CreateLmpStatisticsInput>> {
  return z.object({
    away: z.lazy(() => LmpStatisticsTeamInputSchema().nullish()),
    home: z.lazy(() => LmpStatisticsTeamInputSchema().nullish()),
    matchId: z.string(),
    matchStatisticsList: z.array(z.lazy(() => LmpStatisticsMatchStatisticsInputSchema().nullable())).nullish(),
    status: z.string().nullish(),
  });
}

export function CreateMatchCardInputSchema(): z.ZodObject<Properties<Types.CreateMatchCardInput>> {
  return z.object({
    away: z.lazy(() => MatchCardTeamInputSchema().nullish()),
    competition: z.lazy(() => MatchCardCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => MatchCardTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    status: z.string().nullish(),
  });
}

export function CreateMatchChangeInputSchema(): z.ZodObject<Properties<Types.CreateMatchChangeInput>> {
  return z.object({
    id: z.string().nullish(),
    matchList: z.array(z.lazy(() => MatchChangeMatchInputSchema().nullable())).nullish(),
  });
}

export function CreateMatchCommentInputSchema(): z.ZodObject<Properties<Types.CreateMatchCommentInput>> {
  return z.object({
    commentId: z.string(),
    createdAt: z.string().nullish(),
    matchCommentJson: z.any(),
    matchId: z.string(),
    minute: z.number().nullish(),
    player1Id: z.string().nullish(),
    player2Id: z.string().nullish(),
    seasonAndCompId: z.string(),
    second: z.number().nullish(),
    type: z.string().nullish(),
    updatedAt: z.string().nullish(),
  });
}

export function CreateMatchCommentsInputSchema(): z.ZodObject<Properties<Types.CreateMatchCommentsInput>> {
  return z.object({
    awayTeam: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    comments: z.array(z.lazy(() => MatchCommentsCommentInputSchema().nullable())).nullish(),
    competition: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    firstCommentDate: z.string().nullish(),
    homeTeam: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    lastCommentDate: z.string().nullish(),
    matchId: z.string(),
    matchStatus: z.string().nullish(),
    page: z.number(),
    pagination: z.lazy(() => MatchCommentsPaginationInputSchema().nullish()),
    url: z.string().nullish(),
  });
}

export function CreateMatchDataInputSchema(): z.ZodObject<Properties<Types.CreateMatchDataInput>> {
  return z.object({
    data: z.any(),
    matchId: z.string(),
    messageType: z.string(),
  });
}

export function CreateMatchInputSchema(): z.ZodObject<Properties<Types.CreateMatchInput>> {
  return z.object({
    awayTeamId: z.string(),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    date: z.number(),
    homeTeamId: z.string(),
    matchId: z.string(),
    matchJson: z.any().nullish(),
    refereeId: z.string().nullish(),
    round: z.number(),
    roundGroup: z.string().nullish(),
    scheduleJson: z.any().nullish(),
    seasonAndCompId: z.string(),
    status: z.string(),
  });
}

export function CreateNotificationMatchInputSchema(): z.ZodObject<Properties<Types.CreateNotificationMatchInput>> {
  return z.object({
    away: z.lazy(() => NotificationMatchTeamInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => NotificationMatchTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    matchJson: z.any().nullish(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    updates: z.lazy(() => NotificationMatchUpdatesInputSchema().nullish()),
  });
}

export function CreatePlayerDataInputSchema(): z.ZodObject<Properties<Types.CreatePlayerDataInput>> {
  return z.object({
    action: z.string(),
    competitionId: z.string(),
    data: z.any().nullish(),
    messageType: z.string(),
    playerId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreatePlayerInputSchema(): z.ZodObject<Properties<Types.CreatePlayerInput>> {
  return z.object({
    editorialDataId: z.string().nullish(),
    playerId: z.string(),
    playerJson: z.any(),
    role: z.string().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function CreatePlayerPagesStatsInputSchema(): z.ZodObject<Properties<Types.CreatePlayerPagesStatsInput>> {
  return z.object({
    playerId: z.string(),
    playerRole: z.string(),
    statistics: z.array(z.lazy(() => PlayerPagesStatsSeasonCompStatInputSchema().nullable())).nullish(),
  });
}

export function CreatePlayerRankingInputSchema(): z.ZodObject<Properties<Types.CreatePlayerRankingInput>> {
  return z.object({
    playerId: z.string(),
    playerRankingJson: z.any(),
    position: z.number().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function CreatePlayerStatsInputSchema(): z.ZodObject<Properties<Types.CreatePlayerStatsInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
    statsJson: z.any().nullish(),
  });
}

export function CreatePredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.CreatePredictedLineupDetailsInput>
> {
  return z.object({
    competitionId: z.string(),
    hasHeadshots: z.boolean().nullish(),
    matchData: z.lazy(() => PredictedLineupMatchInputSchema().nullish()),
    predictedHpLink: z.string().nullish(),
    predictedSponsorLink: z.string().nullish(),
    teamId: z.string(),
  });
}

export function CreatePredictedLineupHomepageInputSchema(): z.ZodObject<
  Properties<Types.CreatePredictedLineupHomepageInput>
> {
  return z.object({
    competitionId: z.string(),
    matchList: z.array(z.lazy(() => PredictedLineupMatchInputSchema().nullable())).nullish(),
    round: z.string(),
  });
}

export function CreatePredictedLineupInputSchema(): z.ZodObject<Properties<Types.CreatePredictedLineupInput>> {
  return z.object({
    awayTeamId: z.string(),
    homeTeamId: z.string(),
    matchId: z.string(),
    predictedLineupJson: z.any(),
    round: z.number(),
    seasonAndCompId: z.string(),
    status: z.string(),
  });
}

export function CreatePredictedLineupRoundInputSchema(): z.ZodObject<
  Properties<Types.CreatePredictedLineupRoundInput>
> {
  return z.object({
    round: z.number(),
    seasonAndCompId: z.string(),
  });
}

export function CreateRankingInputSchema(): z.ZodObject<Properties<Types.CreateRankingInput>> {
  return z.object({
    matchDayId: z.number(),
    position: z.number(),
    rankingJson: z.any(),
    roundName: z.string(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function CreateReportDetailsInputSchema(): z.ZodObject<Properties<Types.CreateReportDetailsInput>> {
  return z.object({
    competitionId: z.string(),
    reports: z.array(z.lazy(() => ReportDetailsMatchInputSchema().nullable())).nullish(),
    reportsHomepageUrl: z.string().nullish(),
    round: z.number(),
    seasonId: z.string(),
  });
}

export function CreateReportInputSchema(): z.ZodObject<Properties<Types.CreateReportInput>> {
  return z.object({
    matchId: z.string(),
    reportJson: z.any(),
    seasonAndCompId: z.string(),
    status: z.string(),
  });
}

export function CreateScorersRankingPlayerPageInputSchema(): z.ZodObject<
  Properties<Types.CreateScorersRankingPlayerPageInput>
> {
  return z.object({
    competitionCalendarUrl: z.string().nullish(),
    competitionId: z.string(),
    competitionName: z.string().nullish(),
    competitionSlugName: z.string().nullish(),
    scorers: z.array(z.lazy(() => ScorersRankingInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function CreateSeasonInputSchema(): z.ZodObject<Properties<Types.CreateSeasonInput>> {
  return z.object({
    editorialData: z.any().nullish(),
    id: z.string().nullish(),
  });
}

export function CreateTeamCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.CreateTeamCalendarAndResultsInput>
> {
  return z.object({
    matchDayList: z.array(z.lazy(() => CalendarAndResultsMatchDayListInputSchema().nullable())).nullish(),
    teamId: z.string(),
  });
}

export function CreateTeamInputSchema(): z.ZodObject<Properties<Types.CreateTeamInput>> {
  return z.object({
    editorialDataId: z.string(),
    isPlaceholder: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
    teamJson: z.any(),
  });
}

export function CreateTeamPlayersInputSchema(): z.ZodObject<Properties<Types.CreateTeamPlayersInput>> {
  return z.object({
    competition: z.lazy(() => TeamPlayersCompetitionInputSchema().nullish()),
    players: z.array(z.lazy(() => TeamPlayersPlayerInputSchema().nullable())).nullish(),
    season: z.lazy(() => TeamPlayersSeasonInputSchema().nullish()),
    team: z.lazy(() => TeamPlayersTeamInputSchema().nullish()),
    teamId: z.string(),
  });
}

export function CreateTeamRankingInputSchema(): z.ZodObject<Properties<Types.CreateTeamRankingInput>> {
  return z.object({
    rankingLists: z.array(z.lazy(() => TeamRankingRankingListInputSchema().nullable())).nullish(),
    teamId: z.string(),
  });
}

export function DeleteCalendarAndResultsGliderInputSchema(): z.ZodObject<
  Properties<Types.DeleteCalendarAndResultsGliderInput>
> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCardCompetitionCalendarInputSchema(): z.ZodObject<
  Properties<Types.DeleteCardCompetitionCalendarInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCardCompetitionRankingInputSchema(): z.ZodObject<
  Properties<Types.DeleteCardCompetitionRankingInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCompetitionBoardInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionBoardInput>> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCompetitionCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.DeleteCompetitionCalendarAndResultsInput>
> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCompetitionInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteCompetitionRankingInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionRankingInput>> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCompetitionReportsInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionReportsInput>> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCompetitionScorersRankingInputSchema(): z.ZodObject<
  Properties<Types.DeleteCompetitionScorersRankingInput>
> {
  return z.object({
    competitionId: z.string(),
    page: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCompetitionTeamInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionTeamInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteCrossLinkInputSchema(): z.ZodObject<Properties<Types.DeleteCrossLinkInput>> {
  return z.object({
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteEditorialDataInputSchema(): z.ZodObject<Properties<Types.DeleteEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLastAndNextMatchInputSchema(): z.ZodObject<Properties<Types.DeleteLastAndNextMatchInput>> {
  return z.object({
    teamId: z.string(),
  });
}

export function DeleteLiveScoreInputSchema(): z.ZodObject<Properties<Types.DeleteLiveScoreInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteLmpHeroInputSchema(): z.ZodObject<Properties<Types.DeleteLmpHeroInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpHeroStickyInputSchema(): z.ZodObject<Properties<Types.DeleteLmpHeroStickyInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpLineupInputSchema(): z.ZodObject<Properties<Types.DeleteLmpLineupInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpMatchChangeInputSchema(): z.ZodObject<Properties<Types.DeleteLmpMatchChangeInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpPredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.DeleteLmpPredictedLineupDetailsInput>
> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpPreviewTextInputSchema(): z.ZodObject<Properties<Types.DeleteLmpPreviewTextInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteLmpStatisticsInputSchema(): z.ZodObject<Properties<Types.DeleteLmpStatisticsInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteMatchCardInputSchema(): z.ZodObject<Properties<Types.DeleteMatchCardInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteMatchChangeInputSchema(): z.ZodObject<Properties<Types.DeleteMatchChangeInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteMatchCommentInputSchema(): z.ZodObject<Properties<Types.DeleteMatchCommentInput>> {
  return z.object({
    commentId: z.string(),
    matchId: z.string(),
  });
}

export function DeleteMatchCommentsInputSchema(): z.ZodObject<Properties<Types.DeleteMatchCommentsInput>> {
  return z.object({
    matchId: z.string(),
    page: z.number(),
  });
}

export function DeleteMatchDataInputSchema(): z.ZodObject<Properties<Types.DeleteMatchDataInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeleteMatchInputSchema(): z.ZodObject<Properties<Types.DeleteMatchInput>> {
  return z.object({
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteNotificationMatchInputSchema(): z.ZodObject<Properties<Types.DeleteNotificationMatchInput>> {
  return z.object({
    matchId: z.string(),
  });
}

export function DeletePlayerDataInputSchema(): z.ZodObject<Properties<Types.DeletePlayerDataInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeletePlayerInputSchema(): z.ZodObject<Properties<Types.DeletePlayerInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeletePlayerPagesStatsInputSchema(): z.ZodObject<Properties<Types.DeletePlayerPagesStatsInput>> {
  return z.object({
    playerId: z.string(),
  });
}

export function DeletePlayerRankingInputSchema(): z.ZodObject<Properties<Types.DeletePlayerRankingInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeletePlayerStatsInputSchema(): z.ZodObject<Properties<Types.DeletePlayerStatsInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeletePredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.DeletePredictedLineupDetailsInput>
> {
  return z.object({
    competitionId: z.string(),
    teamId: z.string(),
  });
}

export function DeletePredictedLineupHomepageInputSchema(): z.ZodObject<
  Properties<Types.DeletePredictedLineupHomepageInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeletePredictedLineupInputSchema(): z.ZodObject<Properties<Types.DeletePredictedLineupInput>> {
  return z.object({
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeletePredictedLineupRoundInputSchema(): z.ZodObject<
  Properties<Types.DeletePredictedLineupRoundInput>
> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteRankingInputSchema(): z.ZodObject<Properties<Types.DeleteRankingInput>> {
  return z.object({
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function DeleteReportDetailsInputSchema(): z.ZodObject<Properties<Types.DeleteReportDetailsInput>> {
  return z.object({
    competitionId: z.string(),
    round: z.number(),
    seasonId: z.string(),
  });
}

export function DeleteReportInputSchema(): z.ZodObject<Properties<Types.DeleteReportInput>> {
  return z.object({
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteScorersRankingPlayerPageInputSchema(): z.ZodObject<
  Properties<Types.DeleteScorersRankingPlayerPageInput>
> {
  return z.object({
    competitionId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteSeasonInputSchema(): z.ZodObject<Properties<Types.DeleteSeasonInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteTeamCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.DeleteTeamCalendarAndResultsInput>
> {
  return z.object({
    teamId: z.string(),
  });
}

export function DeleteTeamInputSchema(): z.ZodObject<Properties<Types.DeleteTeamInput>> {
  return z.object({
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function DeleteTeamPlayersInputSchema(): z.ZodObject<Properties<Types.DeleteTeamPlayersInput>> {
  return z.object({
    teamId: z.string(),
  });
}

export function DeleteTeamRankingInputSchema(): z.ZodObject<Properties<Types.DeleteTeamRankingInput>> {
  return z.object({
    teamId: z.string(),
  });
}

export function LastAndNextMatchMatchCompetitionInputSchema(): z.ZodObject<
  Properties<Types.LastAndNextMatchMatchCompetitionInput>
> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function LastAndNextMatchMatchInputSchema(): z.ZodObject<Properties<Types.LastAndNextMatchMatchInput>> {
  return z.object({
    away: z.lazy(() => LastAndNextMatchMatchTeamInputSchema().nullish()),
    competition: z.lazy(() => LastAndNextMatchMatchCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => LastAndNextMatchMatchTeamInputSchema().nullish()),
    id: z.string().nullish(),
    liveOnSky: z.string().nullish(),
    matchDetailUrl: z.string().nullish(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LastAndNextMatchMatchTeamInputSchema(): z.ZodObject<Properties<Types.LastAndNextMatchMatchTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    homepageUrl: z.string().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function LiveScoreDayInputSchema(): z.ZodObject<Properties<Types.LiveScoreDayInput>> {
  return z.object({
    calendarResultsUrl: z.string().nullish(),
    competitionBoardUrl: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    matchRoundList: z.array(z.lazy(() => LiveScoreDayRoundInputSchema().nullable())).nullish(),
    rankingUrl: z.string().nullish(),
    seasonId: z.string(),
  });
}

export function LiveScoreDayMatchDayInputSchema(): z.ZodObject<Properties<Types.LiveScoreDayMatchDayInput>> {
  return z.object({
    date: z.string().nullish(),
    matchList: z.array(z.lazy(() => LiveScoreDayMatchInputSchema().nullable())).nullish(),
  });
}

export function LiveScoreDayMatchInputSchema(): z.ZodObject<Properties<Types.LiveScoreDayMatchInput>> {
  return z.object({
    away: z.lazy(() => LiveScoreTeamInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().nullish(),
    home: z.lazy(() => LiveScoreTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LiveScoreDayRoundInputSchema(): z.ZodObject<Properties<Types.LiveScoreDayRoundInput>> {
  return z.object({
    competitionPhase: z.string().nullish(),
    matchDayList: z.array(z.lazy(() => LiveScoreDayMatchDayInputSchema().nullable())).nullish(),
    round: z.number().nullish(),
  });
}

export function LiveScoreTeamInputSchema(): z.ZodObject<Properties<Types.LiveScoreTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    seoName: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function LmpHeroCompetitionInputSchema(): z.ZodObject<Properties<Types.LmpHeroCompetitionInput>> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function LmpHeroScoreboardInputSchema(): z.ZodObject<Properties<Types.LmpHeroScoreboardInput>> {
  return z.object({
    matchStatus: z.string().nullish(),
    minute: z.number().nullish(),
    player: z.string().nullish(),
    type: z.string().nullish(),
  });
}

export function LmpHeroTeamInputSchema(): z.ZodObject<Properties<Types.LmpHeroTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    homepageUrl: z.string().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    scoreboard: z.array(z.lazy(() => LmpHeroScoreboardInputSchema().nullable())).nullish(),
    seoName: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function LmpLineupEventInputSchema(): z.ZodObject<Properties<Types.LmpLineupEventInput>> {
  return z.object({
    id: z.string().nullish(),
    minutes: z.number().nullish(),
    type: z.string().nullish(),
  });
}

export function LmpLineupManagerInputSchema(): z.ZodObject<Properties<Types.LmpLineupManagerInput>> {
  return z.object({
    fullname: z.string().nullish(),
    id: z.string().nullish(),
    name: z.string().nullish(),
    role: z.string().nullish(),
    surname: z.string().nullish(),
  });
}

export function LmpLineupPlayerDetailsInputSchema(): z.ZodObject<Properties<Types.LmpLineupPlayerDetailsInput>> {
  return z.object({
    formationPlace: z.string().nullish(),
    fullname: z.string().nullish(),
    id: z.string().nullish(),
    jerseyNum: z.number().nullish(),
    name: z.string().nullish(),
    photoUrl: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    role: z.string().nullish(),
    surname: z.string().nullish(),
  });
}

export function LmpLineupPlayerListInputSchema(): z.ZodObject<Properties<Types.LmpLineupPlayerListInput>> {
  return z.object({
    manager: z.lazy(() => LmpLineupManagerInputSchema().nullish()),
    startingLineup: z.array(z.lazy(() => LmpLineupPlayerDetailsInputSchema().nullable())).nullish(),
    substitutes: z.array(z.lazy(() => LmpLineupPlayerDetailsInputSchema().nullable())).nullish(),
  });
}

export function LmpLineupPlayerSubstitutionInputSchema(): z.ZodObject<
  Properties<Types.LmpLineupPlayerSubstitutionInput>
> {
  return z.object({
    minutes: z.number().nullish(),
    playerIdIn: z.string().nullish(),
    playerIdOut: z.string().nullish(),
  });
}

export function LmpLineupRefereeInputSchema(): z.ZodObject<Properties<Types.LmpLineupRefereeInput>> {
  return z.object({
    fullname: z.string().nullish(),
    id: z.string().nullish(),
    name: z.string().nullish(),
    surname: z.string().nullish(),
  });
}

export function LmpLineupTeamInputSchema(): z.ZodObject<Properties<Types.LmpLineupTeamInput>> {
  return z.object({
    formation: z.string().nullish(),
    goals: z.array(z.lazy(() => LmpLineupEventInputSchema().nullable())).nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    playerList: z.lazy(() => LmpLineupPlayerListInputSchema().nullish()),
    scorersCards: z.array(z.lazy(() => LmpLineupEventInputSchema().nullable())).nullish(),
    substitution: z.array(z.lazy(() => LmpLineupPlayerSubstitutionInputSchema().nullable())).nullish(),
    topPlayerId: z.string().nullish(),
  });
}

export function LmpMatchChangeMatchInputSchema(): z.ZodObject<Properties<Types.LmpMatchChangeMatchInput>> {
  return z.object({
    away: z.lazy(() => LmpMatchChangeTeamInputSchema().nullish()),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => LmpMatchChangeTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LmpMatchChangeTeamInputSchema(): z.ZodObject<Properties<Types.LmpMatchChangeTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string(),
    shortName: z.string().nullish(),
  });
}

export function LmpPredictedLineupDetailsPlayerInputSchema(): z.ZodObject<
  Properties<Types.LmpPredictedLineupDetailsPlayerInput>
> {
  return z.object({
    formationPlace: z.string().nullish(),
    fullName: z.string().nullish(),
    id: z.string().nullish(),
    jerseyNum: z.number().nullish(),
    name: z.string().nullish(),
    photoUrl: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    role: z.string().nullish(),
    surname: z.string().nullish(),
  });
}

export function LmpPredictedLineupDetailsPlayerListInputSchema(): z.ZodObject<
  Properties<Types.LmpPredictedLineupDetailsPlayerListInput>
> {
  return z.object({
    disqualifiedPlayers: z.string().nullish(),
    manager: z.string().nullish(),
    potentialPlayers: z.string().nullish(),
    predictedLineup: z.array(z.lazy(() => LmpPredictedLineupDetailsPlayerInputSchema().nullable())).nullish(),
    substitutePlayers: z.string().nullish(),
    unavailablePlayers: z.string().nullish(),
  });
}

export function LmpPredictedLineupDetailsTeamInputSchema(): z.ZodObject<
  Properties<Types.LmpPredictedLineupDetailsTeamInput>
> {
  return z.object({
    customName: z.string().nullish(),
    customSeoName: z.string().nullish(),
    formation: z.string().nullish(),
    id: z.string(),
    logo: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    playerList: z.lazy(() => LmpPredictedLineupDetailsPlayerListInputSchema().nullish()),
    predictedTeamLink: z.string().nullish(),
    teamShortName: z.string().nullish(),
  });
}

export function LmpPreviewTextDataInfoInputSchema(): z.ZodObject<Properties<Types.LmpPreviewTextDataInfoInput>> {
  return z.object({
    link: z.string().nullish(),
    text: z.string().nullish(),
  });
}

export function LmpPreviewTextDataTeamInputSchema(): z.ZodObject<Properties<Types.LmpPreviewTextDataTeamInput>> {
  return z.object({
    coach: z.string().nullish(),
    link: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function LmpStatisticsMatchStatisticsInputSchema(): z.ZodObject<
  Properties<Types.LmpStatisticsMatchStatisticsInput>
> {
  return z.object({
    id: z.string().nullish(),
    value: z.lazy(() => LmpStatisticsValueInputSchema().nullish()),
  });
}

export function LmpStatisticsTeamInputSchema(): z.ZodObject<Properties<Types.LmpStatisticsTeamInput>> {
  return z.object({
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function LmpStatisticsValueInputSchema(): z.ZodObject<Properties<Types.LmpStatisticsValueInput>> {
  return z.object({
    away: z.string().nullish(),
    home: z.string().nullish(),
  });
}

export function MatchCardCompetitionInputSchema(): z.ZodObject<Properties<Types.MatchCardCompetitionInput>> {
  return z.object({
    id: z.string().nullish(),
    name: z.string().nullish(),
  });
}

export function MatchCardTeamInputSchema(): z.ZodObject<Properties<Types.MatchCardTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    homepageUrl: z.string().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    seoName: z.string().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function MatchChangeMatchInputSchema(): z.ZodObject<Properties<Types.MatchChangeMatchInput>> {
  return z.object({
    away: z.lazy(() => MatchChangeTeamInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    customCompetitionName: z.string().nullish(),
    date: z.string().nullish(),
    hasVideoHighlights: z.boolean().nullish(),
    home: z.lazy(() => MatchChangeTeamInputSchema().nullish()),
    id: z.string(),
    link: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function MatchChangeTeamInputSchema(): z.ZodObject<Properties<Types.MatchChangeTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function MatchCommentsCommentInputSchema(): z.ZodObject<Properties<Types.MatchCommentsCommentInput>> {
  return z.object({
    commentId: z.string().nullish(),
    createdAt: z.string().nullish(),
    message: z.string().nullish(),
    time: z.string().nullish(),
    type: z.string().nullish(),
    updatedAt: z.string().nullish(),
  });
}

export function MatchCommentsPaginationInputSchema(): z.ZodObject<Properties<Types.MatchCommentsPaginationInput>> {
  return z.object({
    items: z.number().nullish(),
    itemsPerPage: z.number().nullish(),
    nextLink: z.string().nullish(),
    prevLink: z.string().nullish(),
  });
}

export function ModelBooleanFilterInputSchema(): z.ZodObject<Properties<Types.ModelBooleanFilterInput>> {
  return z.object({
    eq: z.boolean().nullish(),
    ne: z.boolean().nullish(),
  });
}

export function ModelCalendarAndResultsGliderFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCalendarAndResultsGliderFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCalendarAndResultsGliderFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    currentRound: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCalendarAndResultsGliderFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCalendarAndResultsGliderFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCardCompetitionCalendarFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardCompetitionCalendarFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardCompetitionCalendarFilterInputSchema().nullable())).nullish(),
    calendarAndResultsLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardCompetitionCalendarFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardCompetitionCalendarFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelCardCompetitionRankingFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardCompetitionRankingFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardCompetitionRankingFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardCompetitionRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardCompetitionRankingFilterInputSchema().nullable())).nullish(),
    rankingLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionBoardFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionBoardFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionBoardFilterInputSchema().nullable())).nullish(),
    boardSponsorLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionBoardFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionBoardFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelCompetitionCalendarAndResultsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionCalendarAndResultsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionCalendarAndResultsFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    currentRound: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionCalendarAndResultsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionCalendarAndResultsFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionFilterInputSchema(): z.ZodObject<Properties<Types.ModelCompetitionFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    previousSeasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionRankingFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionRankingFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionRankingFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionRankingFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionReportsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionReportsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionReportsFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionReportsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionReportsFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionScorersRankingFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionScorersRankingFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionScorersRankingFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionScorersUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionSeoName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionScorersRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionScorersRankingFilterInputSchema().nullable())).nullish(),
    page: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    pages: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionScorersRankingPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionScorersRankingPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
    between: z.array(z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullable())).nullish(),
    eq: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelCompetitionScorersRankingPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionScorersRankingPrimaryCompositeKeyInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    seasonId: z.string().nullish(),
  });
}

export function ModelCompetitionTeamFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCompetitionTeamFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionTeamFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionTeamFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionTeamFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelCrossLinkFilterInputSchema(): z.ZodObject<Properties<Types.ModelCrossLinkFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCrossLinkFilterInputSchema().nullable())).nullish(),
    crossLinkJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCrossLinkFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCrossLinkFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelEditorialDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelEditorialDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    editorialJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    isPrimary: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelEditorialDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelFloatFilterInputSchema(): z.ZodObject<Properties<Types.ModelFloatFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelGetDayListFilterInputSchema(): z.ZodObject<Properties<Types.ModelGetDayListFilterInput>> {
  return z.object({
    matchByStatus: z.lazy(() => ModelInInputSchema().nullish()),
  });
}

export function ModelIDFilterInputSchema(): z.ZodObject<Properties<Types.ModelIDFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelIDKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIDKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelInInputSchema(): z.ZodObject<Properties<Types.ModelInInput>> {
  return z.object({
    in: z.array(z.string().nullable()).nullish(),
  });
}

export function ModelIntFilterInputSchema(): z.ZodObject<Properties<Types.ModelIntFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelIntKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIntKeyConditionInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
  });
}

export function ModelLastAndNextMatchFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLastAndNextMatchFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLastAndNextMatchFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelLastAndNextMatchFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLastAndNextMatchFilterInputSchema().nullable())).nullish(),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelLiveScoreFilterInputSchema(): z.ZodObject<Properties<Types.ModelLiveScoreFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLiveScoreFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLiveScoreFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLiveScoreFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelLmpHeroFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpHeroFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpHeroFilterInputSchema().nullable())).nullish(),
    competitionPhase: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseRound: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    hasFollowMatch: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    matchDetailUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpHeroFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpHeroFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    roundGroup: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    statusStartDateTime: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    venue: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLmpHeroStickyFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpHeroStickyFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpHeroStickyFilterInputSchema().nullable())).nullish(),
    competitionPhase: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseRound: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchDetailUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpHeroStickyFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpHeroStickyFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    roundGroup: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    statusStartDateTime: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    venue: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLmpLineupFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpLineupFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpLineupFilterInputSchema().nullable())).nullish(),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpLineupFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpLineupFilterInputSchema().nullable())).nullish(),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLmpMatchChangeFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpMatchChangeFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpMatchChangeFilterInputSchema().nullable())).nullish(),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpMatchChangeFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpMatchChangeFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelLmpPredictedLineupDetailsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLmpPredictedLineupDetailsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpPredictedLineupDetailsFilterInputSchema().nullable())).nullish(),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpPredictedLineupDetailsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpPredictedLineupDetailsFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLmpPreviewTextFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpPreviewTextFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpPreviewTextFilterInputSchema().nullable())).nullish(),
    competitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchStartDateTime: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpPreviewTextFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpPreviewTextFilterInputSchema().nullable())).nullish(),
    venue: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLmpStatisticsFilterInputSchema(): z.ZodObject<Properties<Types.ModelLmpStatisticsFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLmpStatisticsFilterInputSchema().nullable())).nullish(),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLmpStatisticsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLmpStatisticsFilterInputSchema().nullable())).nullish(),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelMatchCardFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchCardFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchCardFilterInputSchema().nullable())).nullish(),
    competitionPhase: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseRound: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchDetailUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchCardFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchCardFilterInputSchema().nullable())).nullish(),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelMatchChangeFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchChangeFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchChangeFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchChangeFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchChangeFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelMatchCommentFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchCommentFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchCommentFilterInputSchema().nullable())).nullish(),
    commentId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchCommentJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    minute: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchCommentFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchCommentFilterInputSchema().nullable())).nullish(),
    player1Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    player2Id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    second: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    type: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelMatchCommentsFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchCommentsFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchCommentsFilterInputSchema().nullable())).nullish(),
    firstCommentDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    lastCommentDate: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchStatus: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchCommentsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchCommentsFilterInputSchema().nullable())).nullish(),
    page: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    url: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelMatchDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchDataFilterInputSchema().nullable())).nullish(),
    data: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    messageType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchDataFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelMatchFilterInputSchema(): z.ZodObject<Properties<Types.ModelMatchFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelMatchFilterInputSchema().nullable())).nullish(),
    awayTeamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionPhase: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseRound: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    homeTeamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelMatchFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelMatchFilterInputSchema().nullable())).nullish(),
    refereeId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    roundGroup: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    scheduleJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelNotificationMatchFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelNotificationMatchFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelNotificationMatchFilterInputSchema().nullable())).nullish(),
    competitionPhase: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseRound: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionPhaseWinnerTeamId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchDetailUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelNotificationMatchFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelNotificationMatchFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    roundGroup: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPlayerDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelPlayerDataFilterInput>> {
  return z.object({
    action: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    and: z.array(z.lazy(() => ModelPlayerDataFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    data: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    messageType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPlayerDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerDataFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPlayerFilterInputSchema(): z.ZodObject<Properties<Types.ModelPlayerFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelPlayerFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPlayerFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    role: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelPlayerPagesStatsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelPlayerPagesStatsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelPlayerPagesStatsFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelPlayerPagesStatsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerPagesStatsFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerRole: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPlayerRankingFilterInputSchema(): z.ZodObject<Properties<Types.ModelPlayerRankingFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelPlayerRankingFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelPlayerRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerRankingFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    playerRankingJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    position: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelPlayerStatsFilterInputSchema(): z.ZodObject<Properties<Types.ModelPlayerStatsFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelPlayerStatsFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelPlayerStatsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPlayerStatsFilterInputSchema().nullable())).nullish(),
    playerId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    statsJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPredictedLineupDetailsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelPredictedLineupDetailsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelPredictedLineupDetailsFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    hasHeadshots: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPredictedLineupDetailsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPredictedLineupDetailsFilterInputSchema().nullable())).nullish(),
    predictedHpLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    predictedSponsorLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelPredictedLineupFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelPredictedLineupFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelPredictedLineupFilterInputSchema().nullable())).nullish(),
    awayTeamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    homeTeamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPredictedLineupFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPredictedLineupFilterInputSchema().nullable())).nullish(),
    predictedLineupJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPredictedLineupHomepageFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelPredictedLineupHomepageFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelPredictedLineupHomepageFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelPredictedLineupHomepageFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPredictedLineupHomepageFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelPredictedLineupRoundFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelPredictedLineupRoundFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelPredictedLineupRoundFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelPredictedLineupRoundFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelPredictedLineupRoundFilterInputSchema().nullable())).nullish(),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelRankingFilterInputSchema(): z.ZodObject<Properties<Types.ModelRankingFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelRankingFilterInputSchema().nullable())).nullish(),
    matchDayId: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    not: z.lazy(() => ModelRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelRankingFilterInputSchema().nullable())).nullish(),
    position: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    rankingJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    roundName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelReportDetailsFilterInputSchema(): z.ZodObject<Properties<Types.ModelReportDetailsFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelReportDetailsFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelReportDetailsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelReportDetailsFilterInputSchema().nullable())).nullish(),
    reportsHomepageUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    round: z.lazy(() => ModelIntFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelReportDetailsPrimaryCompositeKeyConditionInputSchema(): z.ZodObject<
  Properties<Types.ModelReportDetailsPrimaryCompositeKeyConditionInput>
> {
  return z.object({
    beginsWith: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
    between: z.array(z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullable())).nullish(),
    eq: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
    ge: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
    gt: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
    le: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
    lt: z.lazy(() => ModelReportDetailsPrimaryCompositeKeyInputSchema().nullish()),
  });
}

export function ModelReportDetailsPrimaryCompositeKeyInputSchema(): z.ZodObject<
  Properties<Types.ModelReportDetailsPrimaryCompositeKeyInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    seasonId: z.string().nullish(),
  });
}

export function ModelReportFilterInputSchema(): z.ZodObject<Properties<Types.ModelReportFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelReportFilterInputSchema().nullable())).nullish(),
    matchId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelReportFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelReportFilterInputSchema().nullable())).nullish(),
    reportJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelScorersRankingPlayerPageFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelScorersRankingPlayerPageFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelScorersRankingPlayerPageFilterInputSchema().nullable())).nullish(),
    competitionCalendarUrl: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionSlugName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelScorersRankingPlayerPageFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelScorersRankingPlayerPageFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSeasonFilterInputSchema(): z.ZodObject<Properties<Types.ModelSeasonFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSeasonFilterInputSchema().nullable())).nullish(),
    editorialData: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSeasonFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSeasonFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelStringFilterInputSchema(): z.ZodObject<Properties<Types.ModelStringFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelTeamCalendarAndResultsFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelTeamCalendarAndResultsFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamCalendarAndResultsFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelTeamCalendarAndResultsFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamCalendarAndResultsFilterInputSchema().nullable())).nullish(),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelTeamFilterInputSchema(): z.ZodObject<Properties<Types.ModelTeamFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    isPlaceholder: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTeamFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelTeamPlayersFilterInputSchema(): z.ZodObject<Properties<Types.ModelTeamPlayersFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamPlayersFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelTeamPlayersFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamPlayersFilterInputSchema().nullable())).nullish(),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelTeamRankingFilterInputSchema(): z.ZodObject<Properties<Types.ModelTeamRankingFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamRankingFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelTeamRankingFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamRankingFilterInputSchema().nullable())).nullish(),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function NotificationMatchTeamInputSchema(): z.ZodObject<Properties<Types.NotificationMatchTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    id: z.string().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    overtimeGoal: z.number().nullish(),
    penaltiesGoal: z.number().nullish(),
    regularTimeGoal: z.number().nullish(),
    shootoutGoal: z.number().nullish(),
    totalGoalLegs: z.number().nullish(),
  });
}

export function NotificationMatchUpdatesInputSchema(): z.ZodObject<Properties<Types.NotificationMatchUpdatesInput>> {
  return z.object({
    goals: z.boolean().nullish(),
    red: z.boolean().nullish(),
    status: z.boolean().nullish(),
    substitutions: z.boolean().nullish(),
    yellow: z.boolean().nullish(),
  });
}

export function PlayerPagesStatsChartStatsInputSchema(): z.ZodObject<
  Properties<Types.PlayerPagesStatsChartStatsInput>
> {
  return z.object({
    failure: z.number().nullish(),
    success: z.number().nullish(),
    type: z.string().nullish(),
  });
}

export function PlayerPagesStatsNumericStatsInputSchema(): z.ZodObject<
  Properties<Types.PlayerPagesStatsNumericStatsInput>
> {
  return z.object({
    type: z.string().nullish(),
    value: z.number().nullish(),
  });
}

export function PlayerPagesStatsSeasonCompStatInputSchema(): z.ZodObject<
  Properties<Types.PlayerPagesStatsSeasonCompStatInput>
> {
  return z.object({
    charts: z.array(z.lazy(() => PlayerPagesStatsChartStatsInputSchema().nullable())).nullish(),
    customCompetitionName: z.string().nullish(),
    customSeasonName: z.string().nullish(),
    seasonAndCompId: z.string().nullish(),
    stats: z.array(z.lazy(() => PlayerPagesStatsNumericStatsInputSchema().nullable())).nullish(),
  });
}

export function PredictedLineupMatchInputSchema(): z.ZodObject<Properties<Types.PredictedLineupMatchInput>> {
  return z.object({
    away: z.lazy(() => PredictedLineupTeamInputSchema().nullish()),
    date: z.string().nullish(),
    home: z.lazy(() => PredictedLineupTeamInputSchema().nullish()),
    lmpLineupLink: z.string().nullish(),
    lmpLink: z.string().nullish(),
    matchId: z.string(),
    status: z.string().nullish(),
  });
}

export function PredictedLineupPlayerInputSchema(): z.ZodObject<Properties<Types.PredictedLineupPlayerInput>> {
  return z.object({
    fallbackPhotoUrl: z.string().nullish(),
    formationPlace: z.string().nullish(),
    fullName: z.string().nullish(),
    id: z.string().nullish(),
    jerseyNum: z.number().nullish(),
    name: z.string().nullish(),
    photoUrl: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    role: z.string().nullish(),
    surname: z.string().nullish(),
  });
}

export function PredictedLineupPlayerListInputSchema(): z.ZodObject<Properties<Types.PredictedLineupPlayerListInput>> {
  return z.object({
    disqualifiedPlayers: z.string().nullish(),
    manager: z.string().nullish(),
    potentialPlayers: z.string().nullish(),
    predictedLineup: z.array(z.lazy(() => PredictedLineupPlayerInputSchema().nullable())).nullish(),
    substitutePlayers: z.string().nullish(),
    unavailablePlayers: z.string().nullish(),
  });
}

export function PredictedLineupTeamInputSchema(): z.ZodObject<Properties<Types.PredictedLineupTeamInput>> {
  return z.object({
    customName: z.string().nullish(),
    customSeoName: z.string().nullish(),
    formation: z.string().nullish(),
    id: z.string(),
    logo: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    playerList: z.lazy(() => PredictedLineupPlayerListInputSchema().nullish()),
    predictedTeamLink: z.string().nullish(),
    teamShortName: z.string().nullish(),
  });
}

export function ReportDetailsMatchInputSchema(): z.ZodObject<Properties<Types.ReportDetailsMatchInput>> {
  return z.object({
    away: z.lazy(() => ReportDetailsMatchTeamInputSchema().nullish()),
    date: z.string().nullish(),
    home: z.lazy(() => ReportDetailsMatchTeamInputSchema().nullish()),
    matchId: z.string().nullish(),
    referees: z.array(z.lazy(() => ReportDetailsMatchTeamPlayerInputSchema().nullable())).nullish(),
    reportStatus: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function ReportDetailsMatchTeamInputSchema(): z.ZodObject<Properties<Types.ReportDetailsMatchTeamInput>> {
  return z.object({
    goal: z.number().nullish(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    negativeLogoUrl: z.string().nullish(),
    playerList: z.lazy(() => ReportDetailsMatchTeamPlayerListInputSchema().nullish()),
    seoName: z.string().nullish(),
    shortname: z.string().nullish(),
    teamId: z.string().nullish(),
  });
}

export function ReportDetailsMatchTeamPlayerInputSchema(): z.ZodObject<
  Properties<Types.ReportDetailsMatchTeamPlayerInput>
> {
  return z.object({
    fullname: z.string().nullish(),
    id: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    role: z.string().nullish(),
    topPlayer: z.boolean().nullish(),
    vote: z.string().nullish(),
  });
}

export function ReportDetailsMatchTeamPlayerListInputSchema(): z.ZodObject<
  Properties<Types.ReportDetailsMatchTeamPlayerListInput>
> {
  return z.object({
    manager: z.lazy(() => ReportDetailsMatchTeamPlayerInputSchema().nullish()),
    startingLineup: z.array(z.lazy(() => ReportDetailsMatchTeamPlayerInputSchema().nullable())).nullish(),
    substitutes: z.array(z.lazy(() => ReportDetailsMatchTeamPlayerInputSchema().nullable())).nullish(),
  });
}

export function ScorersRankingInputSchema(): z.ZodObject<Properties<Types.ScorersRankingInput>> {
  return z.object({
    goal: z.number().nullish(),
    logoUrl: z.string().nullish(),
    playerId: z.string().nullish(),
    playerImageUrl: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    playerSurname: z.string().nullish(),
    position: z.number().nullish(),
    seoTeamName: z.string().nullish(),
    teamId: z.string().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
  });
}

export function SeoCustomDataInputSchema(): z.ZodObject<Properties<Types.SeoCustomDataInput>> {
  return z.object({
    customName: z.string().nullish(),
    seoName: z.string().nullish(),
  });
}

export function TeamPlayersCompetitionInputSchema(): z.ZodObject<Properties<Types.TeamPlayersCompetitionInput>> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    seoName: z.string().nullish(),
  });
}

export function TeamPlayersPlayerInputSchema(): z.ZodObject<Properties<Types.TeamPlayersPlayerInput>> {
  return z.object({
    birthdate: z.string().nullish(),
    flag: z.string().nullish(),
    fullname: z.string().nullish(),
    height: z.number().nullish(),
    id: z.string(),
    jerseyNum: z.number().nullish(),
    name: z.string().nullish(),
    playerPageLink: z.string().nullish(),
    role: z.string().nullish(),
    surname: z.string().nullish(),
    weight: z.number().nullish(),
  });
}

export function TeamPlayersSeasonInputSchema(): z.ZodObject<Properties<Types.TeamPlayersSeasonInput>> {
  return z.object({
    id: z.string(),
    label: z.string().nullish(),
  });
}

export function TeamPlayersTeamInputSchema(): z.ZodObject<Properties<Types.TeamPlayersTeamInput>> {
  return z.object({
    homepageUrl: z.string().nullish(),
    id: z.string(),
    logoUrl: z.string().nullish(),
    name: z.string().nullish(),
    seoName: z.string().nullish(),
  });
}

export function TeamRankingGroupInputSchema(): z.ZodObject<Properties<Types.TeamRankingGroupInput>> {
  return z.object({
    roundName: z.string().nullish(),
    teams: z.array(z.lazy(() => TeamRankingTeamInputSchema().nullable())).nullish(),
  });
}

export function TeamRankingLastMatchesTrendInputSchema(): z.ZodObject<
  Properties<Types.TeamRankingLastMatchesTrendInput>
> {
  return z.object({
    away: z.lazy(() => TeamRankingTrendInputSchema().nullish()),
    home: z.lazy(() => TeamRankingTrendInputSchema().nullish()),
    label: z.string().nullish(),
  });
}

export function TeamRankingRankingListInputSchema(): z.ZodObject<Properties<Types.TeamRankingRankingListInput>> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    groups: z.array(z.lazy(() => TeamRankingGroupInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function TeamRankingTeamInputSchema(): z.ZodObject<Properties<Types.TeamRankingTeamInput>> {
  return z.object({
    games: z.number().nullish(),
    gamesDraw: z.number().nullish(),
    gamesLost: z.number().nullish(),
    gamesWon: z.number().nullish(),
    goalsConceded: z.number().nullish(),
    goalsDifference: z.number().nullish(),
    goalsScored: z.number().nullish(),
    id: z.string(),
    lastMatchesTrend: z.array(z.lazy(() => TeamRankingLastMatchesTrendInputSchema().nullable())).nullish(),
    logoUrl: z.string().nullish(),
    points: z.number().nullish(),
    position: z.number().nullish(),
    qualification: z.string().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
    trend: z.string().nullish(),
  });
}

export function TeamRankingTrendInputSchema(): z.ZodObject<Properties<Types.TeamRankingTrendInput>> {
  return z.object({
    goal: z.number().nullish(),
    name: z.string().nullish(),
  });
}

export function UpdateCalendarAndResultsGliderInputSchema(): z.ZodObject<
  Properties<Types.UpdateCalendarAndResultsGliderInput>
> {
  return z.object({
    competitionId: z.string(),
    currentRound: z.number().nullish(),
    rounds: z.array(z.lazy(() => CalendarAndResultsGliderRoundInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateCardCompetitionCalendarInputSchema(): z.ZodObject<
  Properties<Types.UpdateCardCompetitionCalendarInput>
> {
  return z.object({
    calendarAndResultsLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    matchRoundList: z.array(z.lazy(() => CardCompetitionCalendarMatchRoundInputSchema().nullable())).nullish(),
  });
}

export function UpdateCardCompetitionRankingInputSchema(): z.ZodObject<
  Properties<Types.UpdateCardCompetitionRankingInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    groups: z.array(z.lazy(() => CardCompetitionRankingGroupInputSchema().nullable())).nullish(),
    rankingLink: z.string().nullish(),
  });
}

export function UpdateCompetitionBoardInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionBoardInput>> {
  return z.object({
    boardSponsorLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    matchList: z.array(z.lazy(() => CompetitionBoardPhaseRoundInputSchema().nullable())).nullish(),
  });
}

export function UpdateCompetitionCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.UpdateCompetitionCalendarAndResultsInput>
> {
  return z.object({
    competitionId: z.string(),
    currentRound: z.number().nullish(),
    customCompetitionName: z.string().nullish(),
    matchRoundList: z.array(z.lazy(() => CompetitionCalendarAndResultsMatchRoundInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateCompetitionInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    competitionJson: z.any().nullish(),
    editorialDataId: z.string().nullish(),
    previousSeasonAndCompId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateCompetitionRankingInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionRankingInput>> {
  return z.object({
    competitionId: z.string(),
    rankingLists: z.array(z.lazy(() => CompetitionRankingRankingListInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateCompetitionReportsInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionReportsInput>> {
  return z.object({
    competitionId: z.string(),
    reports: z.array(z.lazy(() => CompetitionReportsListInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateCompetitionScorersRankingInputSchema(): z.ZodObject<
  Properties<Types.UpdateCompetitionScorersRankingInput>
> {
  return z.object({
    competitionId: z.string(),
    competitionScorersUrl: z.string().nullish(),
    competitionSeoName: z.string().nullish(),
    page: z.string(),
    pages: z.array(z.string().nullable()).nullish(),
    scorers: z.array(z.lazy(() => CompetitionScorersRankingScorerInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateCompetitionTeamInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionTeamInput>> {
  return z.object({
    competitions: z.array(z.lazy(() => CompetitionTeamCompetitionInputSchema().nullable())).nullish(),
    id: z.string(),
  });
}

export function UpdateCrossLinkInputSchema(): z.ZodObject<Properties<Types.UpdateCrossLinkInput>> {
  return z.object({
    crossLinkJson: z.any().nullish(),
    matchId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function UpdateEditorialDataInputSchema(): z.ZodObject<Properties<Types.UpdateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    isPrimary: z.boolean().nullish(),
    seasonAndCompId: z.string(),
  });
}

export function UpdateLastAndNextMatchInputSchema(): z.ZodObject<Properties<Types.UpdateLastAndNextMatchInput>> {
  return z.object({
    lastMatch: z.lazy(() => LastAndNextMatchMatchInputSchema().nullish()),
    nextMatch: z.lazy(() => LastAndNextMatchMatchInputSchema().nullish()),
    teamId: z.string(),
  });
}

export function UpdateLiveScoreInputSchema(): z.ZodObject<Properties<Types.UpdateLiveScoreInput>> {
  return z.object({
    currentDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
    id: z.string(),
    nextDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
    previousDay: z.array(z.lazy(() => LiveScoreDayInputSchema().nullable())).nullish(),
  });
}

export function UpdateLmpHeroInputSchema(): z.ZodObject<Properties<Types.UpdateLmpHeroInput>> {
  return z.object({
    away: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    competition: z.lazy(() => LmpHeroCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    hasFollowMatch: z.boolean().nullish(),
    home: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    statusStartDateTime: z.string().datetime().nullish(),
    venue: z.string().nullish(),
  });
}

export function UpdateLmpHeroStickyInputSchema(): z.ZodObject<Properties<Types.UpdateLmpHeroStickyInput>> {
  return z.object({
    away: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    competition: z.lazy(() => LmpHeroCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => LmpHeroTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    statusStartDateTime: z.string().datetime().nullish(),
    venue: z.string().nullish(),
  });
}

export function UpdateLmpLineupInputSchema(): z.ZodObject<Properties<Types.UpdateLmpLineupInput>> {
  return z.object({
    away: z.lazy(() => LmpLineupTeamInputSchema().nullish()),
    home: z.lazy(() => LmpLineupTeamInputSchema().nullish()),
    matchId: z.string(),
    referees: z.array(z.lazy(() => LmpLineupRefereeInputSchema().nullable())).nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateLmpMatchChangeInputSchema(): z.ZodObject<Properties<Types.UpdateLmpMatchChangeInput>> {
  return z.object({
    matchId: z.string(),
    matchList: z.array(z.lazy(() => LmpMatchChangeMatchInputSchema().nullable())).nullish(),
  });
}

export function UpdateLmpPredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.UpdateLmpPredictedLineupDetailsInput>
> {
  return z.object({
    away: z.lazy(() => LmpPredictedLineupDetailsTeamInputSchema().nullish()),
    date: z.string().nullish(),
    home: z.lazy(() => LmpPredictedLineupDetailsTeamInputSchema().nullish()),
    matchId: z.string(),
    round: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateLmpPreviewTextInputSchema(): z.ZodObject<Properties<Types.UpdateLmpPreviewTextInput>> {
  return z.object({
    away: z.lazy(() => LmpPreviewTextDataTeamInputSchema().nullish()),
    calendar: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    competition: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    competitionName: z.string().nullish(),
    home: z.lazy(() => LmpPreviewTextDataTeamInputSchema().nullish()),
    matchId: z.string(),
    matchStartDateTime: z.string().nullish(),
    ranking: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    results: z.lazy(() => LmpPreviewTextDataInfoInputSchema().nullish()),
    venue: z.string().nullish(),
  });
}

export function UpdateLmpStatisticsInputSchema(): z.ZodObject<Properties<Types.UpdateLmpStatisticsInput>> {
  return z.object({
    away: z.lazy(() => LmpStatisticsTeamInputSchema().nullish()),
    home: z.lazy(() => LmpStatisticsTeamInputSchema().nullish()),
    matchId: z.string(),
    matchStatisticsList: z.array(z.lazy(() => LmpStatisticsMatchStatisticsInputSchema().nullable())).nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateMatchCardInputSchema(): z.ZodObject<Properties<Types.UpdateMatchCardInput>> {
  return z.object({
    away: z.lazy(() => MatchCardTeamInputSchema().nullish()),
    competition: z.lazy(() => MatchCardCompetitionInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => MatchCardTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    status: z.string().nullish(),
  });
}

export function UpdateMatchChangeInputSchema(): z.ZodObject<Properties<Types.UpdateMatchChangeInput>> {
  return z.object({
    id: z.string(),
    matchList: z.array(z.lazy(() => MatchChangeMatchInputSchema().nullable())).nullish(),
  });
}

export function UpdateMatchCommentInputSchema(): z.ZodObject<Properties<Types.UpdateMatchCommentInput>> {
  return z.object({
    commentId: z.string(),
    createdAt: z.string().nullish(),
    matchCommentJson: z.any().nullish(),
    matchId: z.string(),
    minute: z.number().nullish(),
    player1Id: z.string().nullish(),
    player2Id: z.string().nullish(),
    seasonAndCompId: z.string().nullish(),
    second: z.number().nullish(),
    type: z.string().nullish(),
    updatedAt: z.string().nullish(),
  });
}

export function UpdateMatchCommentsInputSchema(): z.ZodObject<Properties<Types.UpdateMatchCommentsInput>> {
  return z.object({
    awayTeam: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    comments: z.array(z.lazy(() => MatchCommentsCommentInputSchema().nullable())).nullish(),
    competition: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    firstCommentDate: z.string().nullish(),
    homeTeam: z.lazy(() => SeoCustomDataInputSchema().nullish()),
    lastCommentDate: z.string().nullish(),
    matchId: z.string(),
    matchStatus: z.string().nullish(),
    page: z.number(),
    pagination: z.lazy(() => MatchCommentsPaginationInputSchema().nullish()),
    url: z.string().nullish(),
  });
}

export function UpdateMatchDataInputSchema(): z.ZodObject<Properties<Types.UpdateMatchDataInput>> {
  return z.object({
    data: z.any().nullish(),
    matchId: z.string(),
    messageType: z.string().nullish(),
  });
}

export function UpdateMatchInputSchema(): z.ZodObject<Properties<Types.UpdateMatchInput>> {
  return z.object({
    awayTeamId: z.string().nullish(),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    date: z.number().nullish(),
    homeTeamId: z.string().nullish(),
    matchId: z.string(),
    matchJson: z.any().nullish(),
    refereeId: z.string().nullish(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    scheduleJson: z.any().nullish(),
    seasonAndCompId: z.string(),
    status: z.string().nullish(),
  });
}

export function UpdateNotificationMatchInputSchema(): z.ZodObject<Properties<Types.UpdateNotificationMatchInput>> {
  return z.object({
    away: z.lazy(() => NotificationMatchTeamInputSchema().nullish()),
    competitionPhase: z.string().nullish(),
    competitionPhaseRound: z.string().nullish(),
    competitionPhaseWinnerTeamId: z.string().nullish(),
    date: z.string().datetime().nullish(),
    home: z.lazy(() => NotificationMatchTeamInputSchema().nullish()),
    matchDetailUrl: z.string().nullish(),
    matchId: z.string(),
    matchJson: z.any().nullish(),
    round: z.number().nullish(),
    roundGroup: z.string().nullish(),
    status: z.string().nullish(),
    updates: z.lazy(() => NotificationMatchUpdatesInputSchema().nullish()),
  });
}

export function UpdatePlayerDataInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerDataInput>> {
  return z.object({
    action: z.string().nullish(),
    competitionId: z.string().nullish(),
    data: z.any().nullish(),
    messageType: z.string().nullish(),
    playerId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdatePlayerInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerInput>> {
  return z.object({
    editorialDataId: z.string().nullish(),
    playerId: z.string(),
    playerJson: z.any().nullish(),
    role: z.string().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string().nullish(),
  });
}

export function UpdatePlayerPagesStatsInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerPagesStatsInput>> {
  return z.object({
    playerId: z.string(),
    playerRole: z.string().nullish(),
    statistics: z.array(z.lazy(() => PlayerPagesStatsSeasonCompStatInputSchema().nullable())).nullish(),
  });
}

export function UpdatePlayerRankingInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerRankingInput>> {
  return z.object({
    playerId: z.string(),
    playerRankingJson: z.any().nullish(),
    position: z.number().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string().nullish(),
  });
}

export function UpdatePlayerStatsInputSchema(): z.ZodObject<Properties<Types.UpdatePlayerStatsInput>> {
  return z.object({
    playerId: z.string(),
    seasonAndCompId: z.string(),
    statsJson: z.any().nullish(),
  });
}

export function UpdatePredictedLineupDetailsInputSchema(): z.ZodObject<
  Properties<Types.UpdatePredictedLineupDetailsInput>
> {
  return z.object({
    competitionId: z.string(),
    hasHeadshots: z.boolean().nullish(),
    matchData: z.lazy(() => PredictedLineupMatchInputSchema().nullish()),
    predictedHpLink: z.string().nullish(),
    predictedSponsorLink: z.string().nullish(),
    teamId: z.string(),
  });
}

export function UpdatePredictedLineupHomepageInputSchema(): z.ZodObject<
  Properties<Types.UpdatePredictedLineupHomepageInput>
> {
  return z.object({
    competitionId: z.string(),
    matchList: z.array(z.lazy(() => PredictedLineupMatchInputSchema().nullable())).nullish(),
    round: z.string().nullish(),
  });
}

export function UpdatePredictedLineupInputSchema(): z.ZodObject<Properties<Types.UpdatePredictedLineupInput>> {
  return z.object({
    awayTeamId: z.string().nullish(),
    homeTeamId: z.string().nullish(),
    matchId: z.string(),
    predictedLineupJson: z.any().nullish(),
    round: z.number().nullish(),
    seasonAndCompId: z.string(),
    status: z.string().nullish(),
  });
}

export function UpdatePredictedLineupRoundInputSchema(): z.ZodObject<
  Properties<Types.UpdatePredictedLineupRoundInput>
> {
  return z.object({
    round: z.number().nullish(),
    seasonAndCompId: z.string(),
  });
}

export function UpdateRankingInputSchema(): z.ZodObject<Properties<Types.UpdateRankingInput>> {
  return z.object({
    matchDayId: z.number().nullish(),
    position: z.number().nullish(),
    rankingJson: z.any().nullish(),
    roundName: z.string().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function UpdateReportDetailsInputSchema(): z.ZodObject<Properties<Types.UpdateReportDetailsInput>> {
  return z.object({
    competitionId: z.string(),
    reports: z.array(z.lazy(() => ReportDetailsMatchInputSchema().nullable())).nullish(),
    reportsHomepageUrl: z.string().nullish(),
    round: z.number(),
    seasonId: z.string(),
  });
}

export function UpdateReportInputSchema(): z.ZodObject<Properties<Types.UpdateReportInput>> {
  return z.object({
    matchId: z.string(),
    reportJson: z.any().nullish(),
    seasonAndCompId: z.string(),
    status: z.string().nullish(),
  });
}

export function UpdateScorersRankingPlayerPageInputSchema(): z.ZodObject<
  Properties<Types.UpdateScorersRankingPlayerPageInput>
> {
  return z.object({
    competitionCalendarUrl: z.string().nullish(),
    competitionId: z.string(),
    competitionName: z.string().nullish(),
    competitionSlugName: z.string().nullish(),
    scorers: z.array(z.lazy(() => ScorersRankingInputSchema().nullable())).nullish(),
    seasonId: z.string(),
  });
}

export function UpdateSeasonInputSchema(): z.ZodObject<Properties<Types.UpdateSeasonInput>> {
  return z.object({
    editorialData: z.any().nullish(),
    id: z.string(),
  });
}

export function UpdateTeamCalendarAndResultsInputSchema(): z.ZodObject<
  Properties<Types.UpdateTeamCalendarAndResultsInput>
> {
  return z.object({
    matchDayList: z.array(z.lazy(() => CalendarAndResultsMatchDayListInputSchema().nullable())).nullish(),
    teamId: z.string(),
  });
}

export function UpdateTeamInputSchema(): z.ZodObject<Properties<Types.UpdateTeamInput>> {
  return z.object({
    editorialDataId: z.string().nullish(),
    isPlaceholder: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
    teamJson: z.any().nullish(),
  });
}

export function UpdateTeamPlayersInputSchema(): z.ZodObject<Properties<Types.UpdateTeamPlayersInput>> {
  return z.object({
    competition: z.lazy(() => TeamPlayersCompetitionInputSchema().nullish()),
    players: z.array(z.lazy(() => TeamPlayersPlayerInputSchema().nullable())).nullish(),
    season: z.lazy(() => TeamPlayersSeasonInputSchema().nullish()),
    team: z.lazy(() => TeamPlayersTeamInputSchema().nullish()),
    teamId: z.string(),
  });
}

export function UpdateTeamRankingInputSchema(): z.ZodObject<Properties<Types.UpdateTeamRankingInput>> {
  return z.object({
    rankingLists: z.array(z.lazy(() => TeamRankingRankingListInputSchema().nullable())).nullish(),
    teamId: z.string(),
  });
}
